'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface QualificationStep {
  id: number;
  title: string;
  description: string;
  icon: string;
  status: 'completed' | 'current' | 'upcoming';
  requirements: string[];
}

interface PartnershipQualificationProps {
  className?: string;
}

const PartnershipQualification = ({ className = '' }: PartnershipQualificationProps) => {
  const [activeStep, setActiveStep] = useState(2);

  const steps: QualificationStep[] = [
    {
      id: 1,
      title: "Initial Assessment",
      description: "Technical capability review and facility evaluation",
      icon: "ClipboardDocumentListIcon",
      status: "completed",
      requirements: [
        "Capability questionnaire submission",
        "Quality system documentation review",
        "Facility certification verification",
        "Technical specification alignment"
      ]
    },
    {
      id: 2,
      title: "Documentation Review",
      description: "Comprehensive audit of quality systems and compliance",
      icon: "DocumentCheckIcon",
      status: "current",
      requirements: [
        "ISO certification validation",
        "Process flow documentation",
        "Chemistry protocol verification",
        "Contamination control procedures"
      ]
    },
    {
      id: 3,
      title: "On-Site Audit",
      description: "Physical facility inspection and process validation",
      icon: "BuildingOffice2Icon",
      status: "upcoming",
      requirements: [
        "Cleanroom environment verification",
        "Equipment capability assessment",
        "Quality control system review",
        "Personnel training validation"
      ]
    },
    {
      id: 4,
      title: "Sample Production",
      description: "Trial run with full documentation and testing",
      icon: "CubeIcon",
      status: "upcoming",
      requirements: [
        "First article inspection",
        "Process capability study",
        "Contamination testing",
        "Full traceability demonstration"
      ]
    },
    {
      id: 5,
      title: "Qualification Approval",
      description: "Final review and supplier approval",
      icon: "CheckBadgeIcon",
      status: "upcoming",
      requirements: [
        "Management review meeting",
        "Supplier agreement execution",
        "Quality metrics establishment",
        "Partnership activation"
      ]
    }
  ];

  const getStepColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-success text-white';
      case 'current':
        return 'bg-primary text-white';
      case 'upcoming':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getConnectorColor = (index: number) => {
    if (steps[index].status === 'completed') return 'bg-success';
    if (steps[index].status === 'current') return 'bg-primary';
    return 'bg-border';
  };

  return (
    <section className={`py-14 lg:py-22 bg-gradient-to-b from-muted/30 to-background ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-conversion/10 rounded-full mb-4">
            <Icon name="UserGroupIcon" size={20} variant="solid" className="text-conversion" />
            <span className="text-sm font-cta font-semibold text-conversion">Partnership Process</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
            Partnership Qualification Workflow
          </h2>
          <p className="text-lg text-muted-foreground font-body">
            Structured qualification process with technical assessment capabilities and comprehensive validation.
          </p>
        </div>

        <div className="hidden lg:block mb-16">
          <div className="relative">
            <div className="absolute top-12 left-0 right-0 h-1 bg-border" />
            <div className="relative flex justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex flex-col items-center" style={{ width: `${100 / steps.length}%` }}>
                  <button
                    onClick={() => setActiveStep(step.id)}
                    className={`relative z-10 flex items-center justify-center w-24 h-24 rounded-full transition-all duration-base ${getStepColor(step.status)} ${
                      activeStep === step.id ? 'ring-4 ring-primary/30 scale-110' : 'hover:scale-105'
                    }`}
                  >
                    <Icon name={step.icon as any} size={32} variant={step.status === 'upcoming' ? 'outline' : 'solid'} />
                  </button>
                  <div className="mt-4 text-center">
                    <div className="text-sm font-headline font-bold text-foreground mb-1">
                      Step {step.id}
                    </div>
                    <div className="text-xs text-muted-foreground font-body">
                      {step.title}
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`absolute top-12 h-1 transition-all duration-base ${getConnectorColor(index)}`}
                      style={{
                        left: `${(100 / steps.length) * (index + 0.5)}%`,
                        width: `${100 / steps.length}%`
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:hidden space-y-4 mb-12">
          {steps.map((step) => (
            <button
              key={step.id}
              onClick={() => setActiveStep(step.id)}
              className={`w-full flex items-center space-x-4 p-4 rounded-lg border transition-all duration-base ${
                activeStep === step.id
                  ? 'bg-primary/5 border-primary' :'bg-card border-border hover:bg-muted/50'
              }`}
            >
              <div className={`flex items-center justify-center w-12 h-12 rounded-full flex-shrink-0 ${getStepColor(step.status)}`}>
                <Icon name={step.icon as any} size={24} variant={step.status === 'upcoming' ? 'outline' : 'solid'} />
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm font-headline font-bold text-foreground mb-1">
                  Step {step.id}: {step.title}
                </div>
                <div className="text-xs text-muted-foreground font-body">
                  {step.description}
                </div>
              </div>
              <Icon
                name="ChevronRightIcon"
                size={20}
                variant="outline"
                className={`flex-shrink-0 ${activeStep === step.id ? 'text-primary' : 'text-muted-foreground'}`}
              />
            </button>
          ))}
        </div>

        {steps.map((step) => (
          <div
            key={step.id}
            className={`${activeStep === step.id ? 'block' : 'hidden'}`}
          >
            <div className="bg-card rounded-lg border border-border p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className={`flex items-center justify-center w-16 h-16 rounded-lg flex-shrink-0 ${getStepColor(step.status)}`}>
                  <Icon name={step.icon as any} size={32} variant={step.status === 'upcoming' ? 'outline' : 'solid'} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-2xl font-headline font-bold text-foreground">
                      {step.title}
                    </h3>
                    <div className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-cta font-semibold ${
                      step.status === 'completed' ? 'bg-success/10 text-success' :
                      step.status === 'current'? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                    }`}>
                      {step.status === 'completed' && <Icon name="CheckCircleIcon" size={14} variant="solid" />}
                      {step.status === 'current' && <Icon name="ArrowPathIcon" size={14} variant="outline" />}
                      {step.status === 'upcoming' && <Icon name="ClockIcon" size={14} variant="outline" />}
                      <span className="capitalize">{step.status}</span>
                    </div>
                  </div>
                  <p className="text-base text-muted-foreground font-body">
                    {step.description}
                  </p>
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Icon name="ClipboardDocumentListIcon" size={20} variant="solid" className="text-primary" />
                  <span className="text-sm font-cta font-semibold text-foreground">Requirements & Deliverables</span>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {step.requirements.map((requirement, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <Icon
                        name={step.status === 'completed' ? 'CheckCircleIcon' : 'CircleStackIcon'}
                        size={18}
                        variant={step.status === 'completed' ? 'solid' : 'outline'}
                        className={`mt-0.5 flex-shrink-0 ${
                          step.status === 'completed' ? 'text-success' : 'text-primary'
                        }`}
                      />
                      <span className="text-sm text-foreground font-body">{requirement}</span>
                    </div>
                  ))}
                </div>
              </div>

              {step.status === 'current' && (
                <div className="mt-6 flex flex-col sm:flex-row gap-4">
                  <button className="flex-1 inline-flex items-center justify-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-md font-cta font-bold hover:opacity-90 transition-all duration-base shadow-sm">
                    <Icon name="DocumentArrowUpIcon" size={20} variant="outline" />
                    <span>Submit Documentation</span>
                  </button>
                  <button className="flex-1 inline-flex items-center justify-center space-x-2 px-6 py-3 bg-muted hover:bg-muted/80 text-foreground rounded-md font-cta font-semibold transition-all duration-base">
                    <Icon name="ChatBubbleLeftRightIcon" size={20} variant="outline" />
                    <span>Schedule Discussion</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        <div className="mt-12 grid md:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg p-8 border border-primary/20">
            <div className="flex items-start space-x-4 mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-primary rounded-lg flex-shrink-0">
                <Icon name="ClockIcon" size={24} variant="outline" className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                  Typical Timeline
                </h3>
                <p className="text-sm text-muted-foreground font-body">
                  Complete qualification process typically takes 8-12 weeks from initial assessment to approval
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm font-body">
                <span className="text-muted-foreground">Initial Assessment</span>
                <span className="font-semibold text-foreground">1-2 weeks</span>
              </div>
              <div className="flex items-center justify-between text-sm font-body">
                <span className="text-muted-foreground">Documentation Review</span>
                <span className="font-semibold text-foreground">2-3 weeks</span>
              </div>
              <div className="flex items-center justify-between text-sm font-body">
                <span className="text-muted-foreground">On-Site Audit</span>
                <span className="font-semibold text-foreground">1 week</span>
              </div>
              <div className="flex items-center justify-between text-sm font-body">
                <span className="text-muted-foreground">Sample Production</span>
                <span className="font-semibold text-foreground">3-4 weeks</span>
              </div>
              <div className="flex items-center justify-between text-sm font-body">
                <span className="text-muted-foreground">Final Approval</span>
                <span className="font-semibold text-foreground">1-2 weeks</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-conversion/5 to-accent/5 rounded-lg p-8 border border-conversion/20">
            <div className="flex items-start space-x-4 mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-conversion rounded-lg flex-shrink-0">
                <Icon name="RocketLaunchIcon" size={24} variant="outline" className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                  Start Your Qualification
                </h3>
                <p className="text-sm text-muted-foreground font-body">
                  Begin the partnership process with our technical team
                </p>
              </div>
            </div>
            <button className="w-full inline-flex items-center justify-center space-x-2 px-6 py-3 bg-conversion text-conversion-foreground rounded-md font-cta font-bold hover:opacity-90 transition-all duration-base shadow-sm">
              <Icon name="PaperAirplaneIcon" size={20} variant="solid" />
              <span>Submit Partnership Inquiry</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnershipQualification;